// Serial number validation utilities

/**
 * Validates a Calibrite serial number format
 * @param {string} serialNumber - The serial number to validate
 * @returns {Object} - { isValid: boolean, error: string | null }
 */
export const validateSerialNumber = (serialNumber) => {
  if (!serialNumber) {
    return { isValid: false, error: 'Serial number is required' };
  }

  // Basic prefix check
  if (!serialNumber.startsWith('C9-')) {
    return { isValid: false, error: 'Serial number must start with C9-' };
  }

  // Format check: C9-XXX-XXX-XXX-XXX
  const serialFormat = /^C9-[A-Z0-9]{3}-[A-Z0-9]{3}-[A-Z0-9]{3}-[A-Z0-9]{3}$/;
  if (!serialFormat.test(serialNumber)) {
    return { 
      isValid: false, 
      error: 'Invalid serial number format. Expected format: C9-XXX-XXX-XXX-XXX' 
    };
  }

  return { isValid: true, error: null };
};

/**
 * Validates if a serial number matches the selected product type
 * @param {string} serialNumber - The serial number to validate
 * @param {string} productType - The selected product type
 * @returns {Object} - { isValid: boolean, error: string | null }
 */
export const validateSerialNumberForProduct = (serialNumber, productType) => {
  const { isValid, error } = validateSerialNumber(serialNumber);
  if (!isValid) {
    return { isValid, error };
  }

  // Extract product identifier from serial (second segment)
  const segments = serialNumber.split('-');
  if (segments.length !== 5) {
    return { isValid: false, error: 'Invalid serial number format' };
  }

  const productIdentifier = segments[1];

  // Product type specific validation rules
  const productRules = {
    'Display': ['DIS', 'DSP'],
    'Studio': ['STU', 'STP'],
    'Photo Target': ['PHT', 'PTG'],
    'Video Target': ['VID', 'VTG'],
    'Accessories': ['ACC']
  };

  const validPrefixes = productRules[productType];
  if (!validPrefixes) {
    return { isValid: false, error: 'Unknown product type' };
  }

  if (!validPrefixes.some(prefix => productIdentifier.startsWith(prefix))) {
    return { 
      isValid: false, 
      error: `Invalid serial number for ${productType} product. Expected prefix: ${validPrefixes.join(' or ')}`
    };
  }

  return { isValid: true, error: null };
}; 